import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProvider } from '../lib/provider';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { Avatar } from '../components/Avatar';
import { Copy, Check, Trash2, Trophy, Star } from 'lucide-react';
import type { Quest } from '../lib/provider/types';
import { isToday } from '../lib/utils';

export const ParentFamily: React.FC = () => {
    const provider = useProvider();
    const navigate = useNavigate();
    const { session } = useAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const [copied, setCopied] = useState<'code' | 'link' | 'kid' | 'parent' | null>(null);
    const [showParents, setShowParents] = useState(() => {
        const saved = localStorage.getItem('showParents');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [showInviteCode, setShowInviteCode] = useState(() => {
        const saved = localStorage.getItem('showInviteCode');
        return saved !== null ? JSON.parse(saved) : true;
    });

    // Save states to localStorage whenever they change
    React.useEffect(() => {
        localStorage.setItem('showParents', JSON.stringify(showParents));
    }, [showParents]);

    React.useEffect(() => {
        localStorage.setItem('showInviteCode', JSON.stringify(showInviteCode));
    }, [showInviteCode]);

    // Real-time subscription for quest completions
    useEffect(() => {
        if (!session.user?.household_id || !provider.getSupabaseClient) return;

        const supabase = provider.getSupabaseClient();
        
        // Subscribe to quest completions for all kids in this household
        const subscription = supabase
            .channel('quest_completions_family')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'quest_completions'
                },
                (payload: any) => {
                    console.log('Quest completion change detected for family:', payload);
                    
                    // Refresh relevant queries when completions change
                    queryClient.invalidateQueries({ queryKey: ['kid-completions'] });
                    queryClient.invalidateQueries({ queryKey: ['kid-assigned-quests'] });
                    queryClient.invalidateQueries({ queryKey: ['kid-balances'] });
                }
            )
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [session.user?.household_id, provider, queryClient]);

    const updateMemberLanguage = useMutation({
        mutationFn: async ({ userId, language }: { userId: string; language: 'en' | 'sl' }) => {
            if (!provider.updateMemberLanguage) throw new Error('updateMemberLanguage not supported by provider');
            return provider.updateMemberLanguage(session.user!.household_id, userId, language);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['members'] });
            if (variables.userId === session.user?.id) {
                window.location.reload();
            }
        },
        onError: (err: any) => {
            alert(err?.message || 'Failed to update language');
        }
    });

    const { data: members, isLoading } = useQuery({
        queryKey: ['members', session.user?.household_id],
        queryFn: () => provider.getMembers(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const { data: household } = useQuery({
        queryKey: ['household', session.user?.household_id],
        queryFn: () => provider.getHousehold(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    const updateHouseholdLanguage = useMutation({
        mutationFn: async (language: 'en' | 'sl') => {
            return provider.updateHousehold(session.user!.household_id, { language });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['household'] });
            window.location.reload();
        },
        onError: (err: any) => {
            alert(err?.message || 'Failed to update language');
        }
    });

    // Fetch balances for all kids
    const kids = members?.filter(m => m.role === 'kid') || [];
    const { data: balances } = useQuery({
        queryKey: ['kid-balances', kids.map(k => k.user_id)],
        queryFn: async () => {
            const balancePromises = kids.map(async (kid) => {
                const balance = await provider.getBalance(kid.user_id);
                return { userId: kid.user_id, balance };
            });
            return Promise.all(balancePromises);
        },
        enabled: kids.length > 0,
    });

    // Fetch recent completions for all kids
    const { data: completions } = useQuery({
        queryKey: ['kid-completions', kids.map(k => k.user_id)],
        queryFn: async () => {
            const completionPromises = kids.map(async (kid) => {
                const kidCompletions = await provider.getKidCompletions(kid.user_id);
                return { userId: kid.user_id, completions: kidCompletions };
            });
            return Promise.all(completionPromises);
        },
        enabled: kids.length > 0,
    });

    // Fetch assigned quests for all kids
    const { data: assignedQuests } = useQuery({
        queryKey: ['kid-assigned-quests', kids.map(k => k.user_id)],
        queryFn: async () => {
            const questPromises = kids.map(async (kid) => {
                const kidQuests = await provider.getKidQuests(kid.user_id);
                return { userId: kid.user_id, quests: kidQuests };
            });
            return Promise.all(questPromises);
        },
        enabled: kids.length > 0,
    });

    const getBalanceForKid = (userId: string) => {
        return balances?.find(b => b.userId === userId)?.balance || 0;
    };

    const getRecentCompletionsForKid = (userId: string, limit: number = 3) => {
        const kidCompletions = completions?.find(c => c.userId === userId)?.completions || [];
        return kidCompletions
            .filter(c => c.status === 'approved')
            .sort((a, b) => new Date(b.completed_at).getTime() - new Date(a.completed_at).getTime())
            .slice(0, limit);
    };

    const getNextActivityForKid = (userId: string) => {
        const kidQuests = assignedQuests?.find(q => q.userId === userId)?.quests || [];
        const kidCompletions = completions?.find(c => c.userId === userId)?.completions || [];
        
        // Filter out completed one-time quests
        const approvedOneTimeQuestIds = new Set(
            kidCompletions
                .filter(c => c.status === 'approved' && c.quest.frequency === 'one_time')
                .map(c => c.quest_id)
        );
        
        // Get today's quests (not completed today) - matching KidHome logic
        const getTodaysQuests = () => {
            const activeDailyQuests = kidQuests.filter(q => {
                if (q.frequency !== 'daily') return false;
                if (approvedOneTimeQuestIds.has(q.id)) return false;
                
                const completedToday = kidCompletions.some(c => 
                    c.quest_id === q.id && 
                    (c.status === 'approved' || c.status === 'pending') && 
                    isToday(c.completed_at)
                );
                
                // Include if not completed today
                return !completedToday;
            });

            const activeWeeklyQuests = kidQuests.filter(q => {
                if (q.frequency !== 'weekly') return false;
                if (approvedOneTimeQuestIds.has(q.id)) return false;
                if (typeof q.weekly_day !== 'number') return false;
                if (new Date().getDay() !== q.weekly_day) return false;

                const completedToday = kidCompletions.some(c =>
                    c.quest_id === q.id &&
                    (c.status === 'approved' || c.status === 'pending') &&
                    isToday(c.completed_at)
                );
                return !completedToday;
            });

            const activeOneTimeQuests = kidQuests.filter(q => {
                if (q.frequency !== 'one_time') return false;
                if (approvedOneTimeQuestIds.has(q.id)) return false;
                if (!q.due_date) return false;
                return isToday(q.due_date);
            });
            
            // Sort by relevance: first by whether it's currently time, then by start hour
            const now = new Date();
            const currentHour = now.getHours();
            
            activeDailyQuests.sort((a, b) => {
                const aStart = typeof a.daily_start_hour === 'number' ? a.daily_start_hour : 0;
                const bStart = typeof b.daily_start_hour === 'number' ? b.daily_start_hour : 0;
                const aEnd = typeof a.daily_end_hour === 'number' ? a.daily_end_hour : 24;
                const bEnd = typeof b.daily_end_hour === 'number' ? b.daily_end_hour : 24;
                
                // Priority 1: Quests that are currently active (current hour between start and end)
                const aIsActive = currentHour >= aStart && currentHour <= aEnd;
                const bIsActive = currentHour >= bStart && currentHour <= bEnd;
                if (aIsActive && !bIsActive) return -1;
                if (!aIsActive && bIsActive) return 1;
                
                // Priority 2: Quests that haven't started yet but are coming up soon
                const aIsUpcoming = currentHour < aStart;
                const bIsUpcoming = currentHour < bStart;
                if (aIsUpcoming && !bIsUpcoming) return -1;
                if (!aIsUpcoming && bIsUpcoming) return 1;
                
                // Priority 3: For quests with same status, sort by start hour
                return aStart - bStart;
            });

            activeWeeklyQuests.sort((a, b) => b.points - a.points);
            activeOneTimeQuests.sort((a, b) => b.points - a.points);

            return [...activeDailyQuests, ...activeWeeklyQuests, ...activeOneTimeQuests];
        };
        
        // Get tomorrow's quests (all daily quests for tomorrow)
        const getTomorrowsQuests = () => {
            const tomorrowDailyQuests = kidQuests.filter(q => {
                if (q.frequency !== 'daily') return false;
                if (approvedOneTimeQuestIds.has(q.id)) return false;
                
                // Include tomorrow's quests (even if completed today, they'll show for tomorrow)
                return true;
            });
            
            // Sort by start hour
            tomorrowDailyQuests.sort((a, b) => {
                const aStart = typeof a.daily_start_hour === 'number' ? a.daily_start_hour : 0;
                const bStart = typeof b.daily_start_hour === 'number' ? b.daily_start_hour : 0;
                return aStart - bStart;
            });
            
            return tomorrowDailyQuests;
        };
        
        const todaysQuests = getTodaysQuests();
        const tomorrowsQuests = getTomorrowsQuests();
        
        // Combine quests: always show 5 total, prioritizing today's quests - matching KidHome logic
        const finalQuests: any[] = [];
        const targetCount = 5;
        
        // Add today's quests first
        finalQuests.push(...todaysQuests.map(quest => ({
            ...quest,
            isDuplicate: false
        })));
        
        // If we need more quests, add tomorrow's quests
        if (finalQuests.length < targetCount) {
            const remainingNeeded = targetCount - finalQuests.length;
            const tomorrowsToAdd = tomorrowsQuests
                .filter(quest => !finalQuests.some(fq => fq.id === quest.id))
                .slice(0, remainingNeeded);
            
            finalQuests.push(...tomorrowsToAdd.map(quest => ({
                ...quest,
                isDuplicate: true
            })));
        }
        
        return finalQuests.slice(0, targetCount);
    };

    const getNextActivityLabel = (quest: Quest & { isDuplicate?: boolean }, userId: string) => {
        if (quest.frequency === 'daily') {
            if (typeof quest.daily_end_hour === 'number') {
                const now = new Date();
                const hour = now.getHours();
                if (hour >= quest.daily_end_hour) return 'Tomorrow';
            }
            
            // Check if completed today
            const kidCompletions = completions?.find(c => c.userId === userId)?.completions || [];
            const completedToday = kidCompletions.some(c => 
                c.quest_id === quest.id && 
                c.status === 'approved' && 
                isToday(c.completed_at)
            );
            
            if (completedToday) return 'Tomorrow';
            
            // If this is a duplicate, show as tomorrow
            if (quest.isDuplicate) return 'Tomorrow';
            
            return 'Today';
        }
        
        if (quest.frequency === 'weekly') {
            const today = new Date().getDay();
            const questDay = quest.weekly_day || 1; // Default to Monday
            const daysUntil = questDay - today;
            
            if (daysUntil === 0) return 'Today';
            if (daysUntil === 1) return 'Tomorrow';
            
            // Show specific day name for quests further in the future
            if (daysUntil > 1 && daysUntil <= 6) {
                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                return days[questDay];
            }
            
            // If daysUntil is negative, it's next week
            if (daysUntil < 0) {
                const daysUntilNextWeek = daysUntil + 7;
                if (daysUntilNextWeek === 0) return 'Today (next week)';
                if (daysUntilNextWeek === 1) return 'Tomorrow (next week)';
                
                const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
                return `Next ${days[questDay]}`;
            }
        }
        
        return 'Soon';
    };

    const deleteMember = useMutation({
        mutationFn: (userId: string) => provider.deleteMember(session.user!.household_id, userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['members'] });
        }
    });

    if (isLoading) return <div className="p-8 text-center text-slate-500 font-bold">{t.common.loading}</div>;

    const baseUrl = window.location.origin;
    const householdId = session.user?.household_id || '';
    const inviteLink = `${baseUrl}/onboarding?householdId=${encodeURIComponent(householdId)}`;
    const inviteLinkKid = `${baseUrl}/onboarding?householdId=${encodeURIComponent(householdId)}&role=kid`;
    const inviteLinkParent = `${baseUrl}/onboarding?householdId=${encodeURIComponent(householdId)}&role=parent`;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <h1 className="text-3xl font-bold text-slate-900">{t.parentFamily.title}</h1>
                    <select
                        value={(household?.language as any) || 'en'}
                        onChange={(e) => updateHouseholdLanguage.mutate(e.target.value as any)}
                        className="text-xs font-bold px-2 py-1 rounded border border-slate-200 text-slate-600 bg-white hover:border-indigo-300 ml-2"
                        title="Household default language"
                        disabled={updateHouseholdLanguage.isPending}
                    >
                        <option value="en">EN</option>
                        <option value="sl">SL</option>
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setShowParents(!showParents)}
                        className="text-xs font-bold px-3 py-1.5 rounded border border-slate-200 text-slate-600 bg-white hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    >
                        {showParents ? 'Hide Parents' : 'Show Parents'}
                    </button>
                    <button
                        onClick={() => setShowInviteCode(!showInviteCode)}
                        className="text-xs font-bold px-3 py-1.5 rounded border border-slate-200 text-slate-600 bg-white hover:border-indigo-300 hover:bg-indigo-50 transition-colors"
                    >
                        {showInviteCode ? 'Hide Invite Code' : 'Show Invite Code'}
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {members?.filter(m => showParents || m.role === 'kid').map((member) => (
                    <div
                        key={member.user_id}
                        onClick={() => member.role === 'kid' && navigate(`/parent/family/${encodeURIComponent(member.display_name.toLowerCase().replace(/\s+/g, '-'))}`)}
                        className={`bg-white rounded-xl p-6 border border-slate-200 shadow-sm relative group ${member.role === 'kid' ? 'cursor-pointer hover:border-indigo-400 transition-all' : ''}`}
                    >
                        {member.role === 'kid' ? (
                            <div className="flex flex-col gap-4">
                                <div className="flex items-start gap-4">
                                    <Avatar 
                                        name={member.display_name} 
                                        size="md" 
                                        variant="kid" 
                                    />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-lg font-bold text-slate-900 truncate">{member.display_name}</h3>
                                            <span className="bg-purple-100 text-purple-600 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                                                Kid
                                            </span>
                                            <select
                                                value={(member as any).language || (household?.language as any) || 'en'}
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => {
                                                    e.stopPropagation();
                                                    updateMemberLanguage.mutate({ userId: member.user_id, language: e.target.value as any });
                                                }}
                                                className="text-xs font-bold px-2 py-1 rounded border border-slate-200 text-slate-600 bg-white hover:border-indigo-300 ml-2"
                                                title="Language"
                                                disabled={updateMemberLanguage.isPending}
                                            >
                                                <option value="en">EN</option>
                                                <option value="sl">SL</option>
                                            </select>
                                        </div>
                                        {member.email && (
                                            <p className="text-sm text-slate-500 truncate">{member.email}</p>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Trophy size={16} className="text-amber-500" />
                                        <span className="text-sm font-bold text-amber-700">
                                            {getBalanceForKid(member.user_id)} <span className="text-xs uppercase opacity-70">{t.common.pts}</span>
                                        </span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    {getNextActivityForKid(member.user_id).length > 0 && (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium text-purple-500 uppercase tracking-wider flex items-center gap-1">
                                                <Star size={12} /> Next Activities (5 quests)
                                            </p>
                                            {getNextActivityForKid(member.user_id).map((quest) => (
                                                <div key={quest.id} className="text-xs text-slate-600 bg-indigo-50 rounded px-2 py-1.5 border border-indigo-100">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium truncate flex-1 mr-2">{quest.title}</span>
                                                        <div className="flex items-center gap-1 whitespace-nowrap">
                                                            <span className="text-[10px] font-bold text-purple-600">
                                                                +{quest.points} {t.common.pts}
                                                            </span>
                                                            <span className="text-[10px] text-purple-400">
                                                                · {getNextActivityLabel(quest, member.user_id)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {getRecentCompletionsForKid(member.user_id).length > 0 && (
                                        <div className="space-y-1">
                                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Recent Activity</p>
                                            {getRecentCompletionsForKid(member.user_id).map((completion) => (
                                                <div key={completion.id} className="text-xs text-slate-600 bg-slate-50 rounded px-2 py-1.5 border border-slate-100">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium truncate flex-1 mr-2">{completion.quest.title}</span>
                                                        <span className="text-[10px] text-slate-400 whitespace-nowrap">
                                                            {new Date(completion.completed_at).toLocaleDateString()} {new Date(completion.completed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-4">
                                <Avatar 
                                    name={member.display_name} 
                                    size="md" 
                                    variant="parent" 
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <h3 className="text-lg font-bold text-slate-900 truncate">{member.display_name}</h3>
                                        <span className="bg-amber-100 text-amber-600 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded">
                                            Parent
                                        </span>
                                        <select
                                            value={(member as any).language || (household?.language as any) || 'en'}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={(e) => {
                                                e.stopPropagation();
                                                updateMemberLanguage.mutate({ userId: member.user_id, language: e.target.value as any });
                                            }}
                                            className="text-xs font-bold px-2 py-1 rounded border border-slate-200 text-slate-600 bg-white hover:border-indigo-300 ml-2"
                                            title="Language"
                                            disabled={updateMemberLanguage.isPending}
                                        >
                                            <option value="en">EN</option>
                                            <option value="sl">SL</option>
                                        </select>
                                    </div>
                                    {member.email && (
                                        <p className="text-sm text-slate-500 truncate">{member.email}</p>
                                    )}
                                </div>
                                <div className="ml-auto flex items-center gap-2">
                                    {member.user_id === session.user?.id ? (
                                        <span className="bg-slate-100 text-slate-500 text-[10px] px-2 py-1 rounded-full font-bold uppercase">You</span>
                                    ) : (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (confirm(`Are you sure you want to remove ${member.display_name}?`)) {
                                                    deleteMember.mutate(member.user_id);
                                                }
                                            }}
                                            className="p-2 text-slate-300 hover:text-red-500 transition-colors"
                                            title={t.common.delete}
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {showInviteCode && (
                <div className="mt-8 bg-indigo-50 rounded-xl p-8 border border-indigo-100">
                    <h2 className="text-xl font-bold text-purple-900 mb-2">{t.parentFamily.joinCode}</h2>
                    <p className="text-purple-700 mb-4 opacity-80">{t.parentFamily.joinCodeNotice}</p>
                    <div className="flex items-center gap-2">
                        <code className="bg-white px-4 py-2 rounded border border-purple-200 font-mono text-purple-600 flex-1">
                            {session.user?.household_id}
                        </code>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(session.user?.household_id || '');
                                setCopied('code');
                                setTimeout(() => setCopied(null), 2000);
                            }}
                            className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 font-bold transition-colors flex items-center gap-2"
                        >
                            {copied === 'code' ? <><Check size={16} /> {t.common.done}</> : <><Copy size={16} /> {t.common.save}</>}
                        </button>
                    </div>

                    <div className="mt-4 space-y-3">
                        <div className="flex items-center gap-2">
                            <code className="bg-white px-4 py-2 rounded border border-purple-200 font-mono text-purple-600 flex-1 overflow-x-auto whitespace-nowrap text-xs">
                                {inviteLink}
                            </code>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(inviteLink);
                                    setCopied('link');
                                    setTimeout(() => setCopied(null), 2000);
                                }}
                                className="px-4 py-2 bg-white text-purple-700 rounded border border-purple-200 hover:bg-purple-50 font-bold transition-colors flex items-center gap-2"
                            >
                                {copied === 'link' ? <><Check size={16} /> {t.common.done}</> : <><Copy size={16} /> Link</>}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="flex items-center gap-2">
                                <code className="bg-white px-4 py-2 rounded border border-purple-200 font-mono text-purple-600 flex-1 overflow-x-auto whitespace-nowrap text-xs">
                                    {inviteLinkKid}
                                </code>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(inviteLinkKid);
                                        setCopied('kid');
                                        setTimeout(() => setCopied(null), 2000);
                                    }}
                                    className="px-4 py-2 bg-white text-purple-700 rounded border border-purple-200 hover:bg-purple-50 font-bold transition-colors flex items-center gap-2"
                                >
                                    {copied === 'kid' ? <><Check size={16} /> {t.common.done}</> : <><Copy size={16} /> Kid</>}
                                </button>
                            </div>

                            <div className="flex items-center gap-2">
                                <code className="bg-white px-4 py-2 rounded border border-purple-200 font-mono text-purple-600 flex-1 overflow-x-auto whitespace-nowrap text-xs">
                                    {inviteLinkParent}
                                </code>
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(inviteLinkParent);
                                        setCopied('parent');
                                        setTimeout(() => setCopied(null), 2000);
                                    }}
                                    className="px-4 py-2 bg-white text-purple-700 rounded border border-purple-200 hover:bg-purple-50 font-bold transition-colors flex items-center gap-2"
                                >
                                    {copied === 'parent' ? <><Check size={16} /> {t.common.done}</> : <><Copy size={16} /> Parent</>}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
